import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { RoType } from '../interface/ro-type';
import { first, firstValueFrom, switchMap } from 'rxjs';
import { DataService } from './data.service';
import { BOOLEAN_NUMBER } from '@app/shared/common-enums/boolean-number';
import { resolveChecklistMediaQualityPreset } from '@app/shared/config/checklist-media-quality.config';

interface GetAllOptions {
  cnpj?: string;
  skipInactives?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class RoTypeService {
  private _isFromMatrix = true;


  constructor(
    private _http: HttpClient,
    private _dataService: DataService
  ) { }

  /** GetAll and find by id */
  async get(id: string) {
    const url = `${environment.mkgoURL}/api/v1/companies-actions/ostype/${id}`;
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response: any = await firstValueFrom(this._http.get(url, header).pipe(first()));
    const [apiRoType] = response?.types || [];
    if (!apiRoType) {
      return null;
    }
    return this._complyApp(apiRoType);
  }

  async getAll(options: GetAllOptions = { cnpj: null, skipInactives: false }): Promise<RoType[]> {
    const { cnpj, skipInactives } = options;
    const url = `${environment.mkgoURL}/api/v1/companies-actions/ostype`;
    const header = await firstValueFrom(this._dataService.httpOptions(cnpj || this._isFromMatrix));
    const response = await firstValueFrom(this._http.get<{ types: RoType[] }>(url, header));
    return response.types
      .map(t => this._complyApp(t))
      .filter(t => skipInactives ? t.active !== BOOLEAN_NUMBER.FALSE : t)
      .reverse();
  }

  async add(roType: RoType): Promise<string> {
    const url = `${environment.mkgoURL}/api/v1/companies-actions/ostype`;
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const resp = await firstValueFrom(this._http.post<{ id: string }>(url, this._complyApi(roType), header));
    return resp.id;
  }

  async update(roType: RoType) {
    const url = `${environment.mkgoURL}/api/v1/companies-actions/ostype/${roType.id}`;
    const apiRoType = this._complyApi(roType)
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    return firstValueFrom(this._http.put<{/** API returning an empty object */}>(url, apiRoType, header));
  }

  /**
   * @deprecated
   * Avoid to delete the OS type, prefer to deactivate it instead, 
   * because of the possible existing relations with OS and financial titles.
   * 
   * @see RoTypeService.deactivate()
   */
  async delete(id: string): Promise<RoType> {
    const url = `${environment.mkgoURL}/api/v1/companies-actions/ostype/${id}`;
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    return firstValueFrom(this._http.delete(url, header));
  }

  deactivate(id: string) {
    const url = `${environment.mkgoURL}/api/v1/companies-actions/ostype/${id}`;
    return this._dataService.httpOptions(this._isFromMatrix)
      .pipe(
        switchMap(options => this._http.put<{}>(url, { active: BOOLEAN_NUMBER.FALSE }, options)),
      );
  }

  reactivate(id: string) {
    const url = `${environment.mkgoURL}/api/v1/companies-actions/ostype/${id}`;
    return this._dataService.httpOptions(this._isFromMatrix)
      .pipe(
        switchMap(options => this._http.put<{}>(url, { active: BOOLEAN_NUMBER.TRUE }, options)),
      );
  }

  private _complyApi(roType: RoType) {
    return {
      "code": roType.code,
      "type": roType.type,
      "observation": roType.observation || "",
      "nfseCnpj": roType.nfseCnpj || "",
      "nfeCnpj": roType.nfeCnpj || "",
      "separateFinancial": roType.separateFinancial !== undefined ? roType.separateFinancial : true,
      "operation": roType.operation?.trim()?.length ? roType.operation : null,
      "checklistAssig": roType.checklistAssig,
      "checklistMediaQualityPreset": resolveChecklistMediaQualityPreset(roType.checklistMediaQualityPreset),
      "osTypeDefault": roType.osTypeDefault,
      "active": roType.active
    }
  }

  private _complyApp(apiRoType: RoType): RoType {
    return {
      id: apiRoType.id,
      active: 'active' in apiRoType ? apiRoType.active : BOOLEAN_NUMBER.TRUE,
      code: apiRoType.code,
      type: apiRoType.type,
      observation: apiRoType.observation?.trim() || "",
      nfseCnpj: apiRoType.nfseCnpj?.trim() || "",
      nfeCnpj: apiRoType.nfeCnpj?.trim() || "",
      separateFinancial: apiRoType.separateFinancial !== undefined ? apiRoType.separateFinancial : true,
      operation: apiRoType.operation || null,
      checklistAssig: apiRoType.checklistAssig,
      checklistMediaQualityPreset: resolveChecklistMediaQualityPreset(apiRoType.checklistMediaQualityPreset),
      osTypeDefault: apiRoType.osTypeDefault
    }
  }

}
